import React, { useCallback, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import type { Location } from 'react-router-dom';

import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import {
  DataGridPro,
  GridCellModes,
  type GridCellModesModel,
  type GridCellParams,
  type GridRowModel,
} from '@mui/x-data-grid-pro';

import {
  useBulkSaveCharterFeederPrices,
  useGetCharterFeederPrices,
} from '~/hooks/apis/flights-charter/useCharterFeederPrices';
import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import { getDayJs } from '~/services/TimeService';

import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';

import {
  type AddRowFormState,
  DEFAULT_ADD_ROW_FORM,
  FEEDER_CODE_PATTERN,
  ISO_DATE_PATTERN,
  ROUTE_AIRPORT_CODE_PATTERN,
  isDefaultFeederOriginCode,
} from './CharterFeederPricesConstants';
import {
  addFeederColumnToRows,
  buildCharterFeederBulkPayload,
  clearCharterFeederRowValues,
  createEmptyCharterFeederRow,
  hasCharterFeederBulkChanges,
  isMelFeederOriginCode,
  isSameRouteRow,
  normalizeFeederCode,
  removeFeederColumnFromRows,
  sortCharterFeederRows,
  toFeederBaseField,
  toFeederBufferField,
} from './CharterFeederPricesHelpers';
import { CHARTER_FEEDER_DATE_FIELD, type CharterFeederSheetRow } from './CharterFeederPricesTypes';
import AddRowDialog from './components/AddRowDialog';
import CharterFeederToolbar from './components/CharterFeederToolbar';
import DeleteColumnDialog from './components/DeleteColumnDialog';
import DeleteRowDialog from './components/DeleteRowDialog';
import DiscardChangesDialog from './components/DiscardChangesDialog';
import { useCharterFeederGridConfig } from './useCharterFeederGridConfig';
import { useCharterFeederSheet } from './useCharterFeederSheet';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function normalizeEditablePrice(value: unknown, feederCode: string): number | null {
  if (value === '' || value === null || value === undefined) {
    return null;
  }

  const parsed = typeof value === 'number' ? value : Number(String(value).trim());
  if (!Number.isFinite(parsed)) {
    throw new Error(`Invalid numeric value for feeder ${feederCode}`);
  }

  return parsed;
}

function normalizeEditableDate(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  const normalized = String(value).trim();
  if (normalized === '') {
    return '';
  }

  if (!ISO_DATE_PATTERN.test(normalized)) {
    throw new Error('Date must use YYYY-MM-DD format.');
  }

  const parsed = getDayJs(normalized);
  if (!parsed.isValid() || parsed.format('YYYY-MM-DD') !== normalized) {
    throw new Error('Date is invalid.');
  }

  return normalized;
}

function createNewRowId(): string {
  return `new-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default function CharterFeederPricesPage(): React.JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const { paginationModel, setPaginationModel } = useDataGridPagination({ page: 0, pageSize: 25 });

  const feederPricesQuery = useGetCharterFeederPrices();

  const { rows, columns, originalRows, hasUnsavedChanges, setRows, setColumns, resetDraft, allowNextSync } =
    useCharterFeederSheet(feederPricesQuery.data);

  // Refs always reflect latest rows/columns so handleSave doesn't see stale state
  // when processRowUpdate's setRows call hasn't been applied yet (click-Save-while-editing race).
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const columnsRef = useRef(columns);
  columnsRef.current = columns;

  const bulkSaveMutation = useBulkSaveCharterFeederPrices({
    onSuccess: allowNextSync,
  });

  const [isViewMode, setIsViewMode] = useState(true);
  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});
  const [newFeederCode, setNewFeederCode] = useState('');
  const [isAddRowDialogOpen, setIsAddRowDialogOpen] = useState(false);
  const [addRowForm, setAddRowForm] = useState<AddRowFormState>(DEFAULT_ADD_ROW_FORM);
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const [rowPendingClear, setRowPendingClear] = useState<CharterFeederSheetRow | null>(null);
  const [columnPendingDelete, setColumnPendingDelete] = useState<string | null>(null);
  const [pendingNavigation, setPendingNavigation] = useState<Location | null>(null);

  const handleNavigationAttempt = useCallback((location: Location) => {
    setPendingNavigation(location);
    setIsDiscardDialogOpen(true);
  }, []);

  const navigationGuard = useUnsavedChangesGuard({
    hasUnsavedChanges,
    onNavigationAttempt: handleNavigationAttempt,
  });

  const handleRequestClearRow = useCallback((row: CharterFeederSheetRow) => {
    setRowPendingClear(row);
  }, []);

  const handleConfirmClearRow = useCallback(() => {
    if (!rowPendingClear) {
      return;
    }

    const nextRows = rowsRef.current.map((row) =>
      row.id === rowPendingClear.id ? clearCharterFeederRowValues(row, columnsRef.current) : row,
    );
    rowsRef.current = nextRows;
    setRows(nextRows);
    setRowPendingClear(null);
  }, [columnsRef, rowPendingClear, rowsRef, setRows]);

  const handleDeleteFeederColumn = useCallback((feederCode: string) => {
    if (isDefaultFeederOriginCode(feederCode)) {
      return;
    }

    setColumnPendingDelete(feederCode);
  }, []);

  const handleConfirmDeleteColumn = useCallback(() => {
    if (!columnPendingDelete) {
      return;
    }

    if (isDefaultFeederOriginCode(columnPendingDelete)) {
      setColumnPendingDelete(null);
      return;
    }

    const nextColumns = columnsRef.current.filter((column) => column.code !== columnPendingDelete);
    columnsRef.current = nextColumns;
    setColumns(nextColumns);

    const nextRows = removeFeederColumnFromRows(rowsRef.current, columnPendingDelete);
    rowsRef.current = nextRows;
    setRows(nextRows);
    setColumnPendingDelete(null);
  }, [columnPendingDelete, columnsRef, rowsRef, setColumns, setRows]);

  const { gridColumns, columnGroupingModel, editableFields, gridSx } = useCharterFeederGridConfig({
    columns,
    isViewMode,
    onRequestClearRow: handleRequestClearRow,
    onDeleteFeederColumn: handleDeleteFeederColumn,
  });

  const handleCellClick = useCallback(
    (params: GridCellParams) => {
      if (isViewMode || !editableFields.has(params.field)) {
        return;
      }

      setCellModesModel({ [params.id]: { [params.field]: { mode: GridCellModes.Edit } } });
    },
    [editableFields, isViewMode],
  );

  const processRowUpdate = useCallback(
    (incomingRow: GridRowModel<CharterFeederSheetRow>) => {
      const nextRow: CharterFeederSheetRow = { ...incomingRow };

      for (const column of columnsRef.current) {
        const baseField = toFeederBaseField(column.code);
        const bufferField = toFeederBufferField(column.code);
        nextRow[baseField] = normalizeEditablePrice(nextRow[baseField], column.code);
        nextRow[bufferField] = isMelFeederOriginCode(column.code)
          ? 0
          : normalizeEditablePrice(nextRow[bufferField], column.code);
      }

      nextRow[CHARTER_FEEDER_DATE_FIELD] = normalizeEditableDate(nextRow[CHARTER_FEEDER_DATE_FIELD]);

      // Update rowsRef synchronously so handleSave reads the correct value even when
      // Save is clicked while a cell is still in edit mode (before setRows re-renders).
      rowsRef.current = rowsRef.current.map((row) => (row.id === nextRow.id ? nextRow : row));
      setRows(rowsRef.current);
      return nextRow;
    },
    [columnsRef, rowsRef, setRows],
  );

  const handleEnterEditMode = useCallback(() => setIsViewMode(false), []);

  const handleExitEditMode = useCallback(() => {
    if (!hasUnsavedChanges) {
      setIsViewMode(true);
      return;
    }

    setIsDiscardDialogOpen(true);
  }, [hasUnsavedChanges]);

  const handleConfirmDiscard = useCallback(() => {
    resetDraft();
    setCellModesModel({});
    setIsDiscardDialogOpen(false);
    setIsViewMode(true);

    if (pendingNavigation) {
      const target = pendingNavigation;
      setPendingNavigation(null);
      navigationGuard.releaseAndNavigate(target);
    }
  }, [navigationGuard, pendingNavigation, resetDraft]);

  const handleCancelDiscard = useCallback(() => {
    setIsDiscardDialogOpen(false);
    setPendingNavigation(null);
  }, []);

  const handleAddFeederColumn = useCallback(() => {
    const feederCode = normalizeFeederCode(newFeederCode);
    if (!FEEDER_CODE_PATTERN.test(feederCode)) {
      enqueueSnackbar('Feeder origin code must have exactly 3 non-whitespace characters.', { variant: 'warning' });
      return;
    }

    if (columnsRef.current.some((column) => column.code === feederCode)) {
      enqueueSnackbar(`Feeder origin ${feederCode} already exists.`, { variant: 'warning' });
      return;
    }

    const nextColumns = [...columnsRef.current, { code: feederCode, isNew: true }];
    columnsRef.current = nextColumns;
    setColumns(nextColumns);

    const nextRows = addFeederColumnToRows(rowsRef.current, feederCode);
    rowsRef.current = nextRows;
    setRows(nextRows);
    setNewFeederCode('');
  }, [columnsRef, enqueueSnackbar, newFeederCode, rowsRef, setColumns, setRows]);

  const resetAddRowDialog = useCallback(() => {
    setAddRowForm(DEFAULT_ADD_ROW_FORM);
    setIsAddRowDialogOpen(false);
  }, []);

  const handleConfirmAddRow = useCallback(() => {
    const date = addRowForm.date?.isValid() ? addRowForm.date.format('YYYY-MM-DD') : '';
    if (!date) {
      enqueueSnackbar('A valid date is required.', { variant: 'warning' });
      return;
    }

    let originAirportCode;
    let destinationAirportCode;

    if (addRowForm.direction === 'xToMel') {
      originAirportCode = 'ALL';
      destinationAirportCode = 'MEL';
    } else if (addRowForm.direction === 'melToX') {
      originAirportCode = 'MEL';
      destinationAirportCode = 'ALL';
    } else {
      originAirportCode = normalizeFeederCode(addRowForm.customOriginAirportCode);
      destinationAirportCode = normalizeFeederCode(addRowForm.customDestinationAirportCode);

      if (!ROUTE_AIRPORT_CODE_PATTERN.test(originAirportCode)) {
        enqueueSnackbar('Origin Airport must have exactly 3 non-whitespace characters.', { variant: 'warning' });
        return;
      }

      if (!ROUTE_AIRPORT_CODE_PATTERN.test(destinationAirportCode)) {
        enqueueSnackbar('Destination Airport must have exactly 3 non-whitespace characters.', { variant: 'warning' });
        return;
      }
    }

    const candidate = { date, originAirportCode, destinationAirportCode };
    if (rowsRef.current.some((row) => isSameRouteRow(row, candidate))) {
      enqueueSnackbar('A row with the same Date, Origin Airport, and Destination Airport already exists.', {
        variant: 'warning',
      });
      return;
    }

    const nextRow = createEmptyCharterFeederRow(createNewRowId(), columnsRef.current, candidate);
    const nextRows = sortCharterFeederRows([...rowsRef.current, nextRow]);
    rowsRef.current = nextRows;
    setRows(nextRows);
    resetAddRowDialog();
  }, [addRowForm, columnsRef, enqueueSnackbar, resetAddRowDialog, rowsRef, setRows]);

  const handleSave = useCallback(async () => {
    const payloadResult = buildCharterFeederBulkPayload({
      rows: rowsRef.current,
      columns: columnsRef.current,
      originalRows,
    });

    if (payloadResult.validationErrors.length > 0) {
      const count = payloadResult.validationErrors.length;
      const summary =
        count === 1
          ? payloadResult.validationErrors[0]
          : `${count} validation errors. First: ${payloadResult.validationErrors[0]}`;
      enqueueSnackbar(summary, { variant: 'error' });
      return;
    }

    if (!hasCharterFeederBulkChanges(payloadResult.payload)) {
      enqueueSnackbar('No changes to save.', { variant: 'warning' });
      return;
    }

    try {
      await bulkSaveMutation.mutateAsync(payloadResult.payload);
      enqueueSnackbar('Changes saved successfully.', { variant: 'success' });
      setIsViewMode(true);
      setCellModesModel({});
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to save Charter feeder prices.', {
        variant: 'error',
      });
    }
  }, [bulkSaveMutation, columnsRef, enqueueSnackbar, originalRows, rowsRef]);

  const isLoading = feederPricesQuery.isLoading;

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="h4" fontWeight={600}>
          Charter Feeder Prices
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage feeder base and buffer prices as a route/date matrix.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Outbound Charter (MEL-MLE): feeder flights must arrive in MEL 3 to 12 hours before the selected charter
          departs. Depending on the charter departure time, feeder flights may arrive on the same day as the charter
          departure or on the previous day.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Inbound Charter (MLE-MEL): feeder flights must depart from MEL 3 to 12 hours after the selected charter
          arrives in MEL. Depending on the charter arrival time, feeder flights may depart on the same day as the
          charter arrival or on the following day.
        </Typography>
      </Stack>

      {feederPricesQuery.isError && (
        <Alert severity="error">
          {feederPricesQuery.error instanceof Error
            ? feederPricesQuery.error.message
            : 'Failed to load Charter feeder prices.'}
        </Alert>
      )}

      <CharterFeederToolbar
        isViewMode={isViewMode}
        isSaving={bulkSaveMutation.isPending}
        isDataLoading={feederPricesQuery.isLoading}
        newFeederCode={newFeederCode}
        onNewFeederCodeChange={setNewFeederCode}
        onEnterEditMode={handleEnterEditMode}
        onExitEditMode={handleExitEditMode}
        onSave={handleSave}
        onAddFeederColumn={handleAddFeederColumn}
        onOpenAddRowDialog={() => setIsAddRowDialogOpen(true)}
      />

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <DataGridPro
            rows={rows}
            columns={gridColumns}
            columnGroupingModel={columnGroupingModel}
            pagination
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            editMode="cell"
            isCellEditable={(params: GridCellParams) => !isViewMode && editableFields.has(params.field)}
            cellModesModel={cellModesModel}
            onCellModesModelChange={setCellModesModel}
            onCellClick={handleCellClick}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={(error: unknown) =>
              enqueueSnackbar(error instanceof Error ? error.message : 'Invalid value.', { variant: 'error' })
            }
            disableRowSelectionOnClick
            sx={gridSx}
          />
        </Box>
      )}

      {!isLoading && rows.length === 0 && (
        <Alert severity="info">No feeder pricing rows found. Switch to Edit mode to add rows and feeder columns.</Alert>
      )}

      <DeleteRowDialog
        row={rowPendingClear}
        onCancel={() => setRowPendingClear(null)}
        onConfirm={handleConfirmClearRow}
      />

      <DeleteColumnDialog
        feederCode={columnPendingDelete}
        onCancel={() => setColumnPendingDelete(null)}
        onConfirm={handleConfirmDeleteColumn}
      />

      <DiscardChangesDialog
        open={isDiscardDialogOpen}
        onDiscard={handleConfirmDiscard}
        onCancel={handleCancelDiscard}
      />

      <AddRowDialog
        open={isAddRowDialogOpen}
        form={addRowForm}
        onChange={setAddRowForm}
        onCancel={resetAddRowDialog}
        onConfirm={handleConfirmAddRow}
      />
    </Stack>
  );
}
